import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Mail, Package, Loader } from "lucide-react";
import { supabase } from "../lib/supabase";
import { formatEuro } from "../lib/utils";
import { productImage } from "../lib/productImage";
import { useCartStore } from "../store/cartStore";

export default function CheckoutSuccess() {
	const [searchParams] = useSearchParams();
	const [status, setStatus] = useState<"loading" | "success" | "error">(
		"loading",
	);
	const [orderDetails, setOrderDetails] = useState<any>(null);

	const sessionId = searchParams.get("session_id");
	const orderId = searchParams.get("order_id");
	const cancelled = searchParams.get("cancelled");

	// Solo a pagamento confermato: chi annulla o ha un errore ritrova il carrello.
	useEffect(() => {
		if (status === "success") useCartStore.getState().clearCart();
	}, [status]);

	useEffect(() => {
		const verifyPayment = async () => {
			// Se è stato cancellato
			if (cancelled === "true") {
				setStatus("error");
				return;
			}

			// Se non c'è session_id o order_id, errore
			if (!sessionId || !orderId) {
				setStatus("error");
				return;
			}

			try {
				const {
					data: { user },
				} = await supabase.auth.getUser();

				// Verifica lo stato dell'ordine nel database
				const { data: order, error } = await supabase
					.from("orders")
					.select(`
            *,
            order_items (
              id,
              quantity,
              price,
              product_id,
              variant_label,
              products (
                name,
                image_url
              )
            )
          `)
					.eq("id", orderId)
					.single();

				if (error) {
					console.error("Errore recupero ordine:", error);

					// In modalità guest il fetch può essere bloccato da RLS: mostriamo comunque conferma
					// perché Stripe ha già effettuato il redirect su success_url.
					if (!user) {
						setStatus("success");
						return;
					}

					setStatus("error");
					return;
				}

				setOrderDetails(order);

				// Se il pagamento è già stato processato (paid), mostra successo
				if (order.payment_status === "paid") {
					setStatus("success");
				} else if (
					order.payment_status === "failed" ||
					order.payment_status === "cancelled"
				) {
					setStatus("error");
				} else {
					// Se è ancora pending, controlla Stripe
					// Il webhook dovrebbe averlo già aggiornato, ma diamo qualche secondo
					setTimeout(() => {
						// Ricontrolla lo stato
						supabase
							.from("orders")
							.select("payment_status")
							.eq("id", orderId)
							.single()
							.then(({ data }) => {
								if (data?.payment_status === "paid") {
									setStatus("success");
								} else {
									setStatus("error");
								}
							});
					}, 2000);
				}
			} catch (error) {
				console.error("Errore verifica pagamento:", error);
				setStatus("error");
			}
		};

		verifyPayment();
	}, [sessionId, orderId, cancelled]);

	// Pagina di loading
	if (status === "loading") {
		return (
			<div className="min-h-screen flex items-center justify-center bg-muted">
				<div className="text-center">
					<Loader className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
					<h2 className="text-2xl font-semibold text-foreground mb-2">
						Verifica del pagamento in corso...
					</h2>
					<p className="text-muted-foreground">
						Attendere prego, stiamo verificando il tuo ordine.
					</p>
				</div>
			</div>
		);
	}

	// Pagina di successo
	if (status === "success") {
		return (
			<div className="min-h-screen bg-gradient-to-br from-eco/30 to-background py-12 px-4">
				<div className="max-w-3xl mx-auto">
					<div className="bg-card rounded-2xl shadow-2xl p-8 md:p-12">
						{/* Icona di successo */}
						<div className="text-center mb-8">
							<div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 dark:bg-green-900 rounded-full mb-6">
								<CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400" />
							</div>

							<h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
								Pagamento Completato con Successo! 🎉
							</h1>

							<p className="text-lg text-muted-foreground mb-2">
								Grazie per il tuo acquisto!
							</p>

							{orderDetails && (
								<div className="inline-block bg-green-50 dark:bg-green-900/30 px-6 py-3 rounded-lg mt-4">
									<p className="text-sm text-muted-foreground">
										Numero Ordine
									</p>
									<p className="text-2xl font-bold text-green-600 dark:text-green-400">
										#{orderDetails.id.slice(-8).toUpperCase()}
									</p>
								</div>
							)}
						</div>

						{/* Informazioni email */}
						<div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
							<div className="flex items-start">
								<Mail className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 mr-4 flex-shrink-0" />
								<div>
									<h3 className="text-lg font-semibold text-foreground mb-2">
										Controlla la tua email
									</h3>
									<p className="text-foreground/80 mb-2">
										Ti abbiamo inviato una email di conferma con il riepilogo
										completo del tuo ordine a:
									</p>
									{orderDetails?.customer_email && (
										<p className="font-semibold text-blue-600 dark:text-blue-400">
											{orderDetails.customer_email}
										</p>
									)}
									<p className="text-sm text-muted-foreground mt-2">
										Se non la trovi, controlla anche nella cartella spam.
									</p>
								</div>
							</div>
						</div>

						{/* Prossimi passi */}
						<div className="bg-muted p-6 rounded-lg mb-8">
							<div className="flex items-start mb-4">
								<Package className="w-6 h-6 text-amber-600 dark:text-amber-400 mt-1 mr-4 flex-shrink-0" />
								<div>
									<h3 className="text-lg font-semibold text-foreground mb-2">
										Cosa succede ora?
									</h3>
									<ul className="space-y-2 text-foreground/80">
										<li className="flex items-start">
											<span className="text-green-500 mr-2">✓</span>
											<span>Il tuo ordine è stato ricevuto e confermato</span>
										</li>
										<li className="flex items-start">
											<span className="text-green-500 mr-2">✓</span>
											<span>
												Inizieremo a preparare il tuo ordine immediatamente
											</span>
										</li>
										<li className="flex items-start">
											<span className="text-amber-500 mr-2">⏳</span>
											<span>
												Riceverai un'altra email quando spediremo il pacco con
												il numero di tracking
											</span>
										</li>
										<li className="flex items-start">
											<span className="text-amber-500 mr-2">⏳</span>
											<span>
												Puoi seguire lo stato del tuo ordine nella sezione "I
												Miei Ordini"
											</span>
										</li>
									</ul>
								</div>
							</div>
						</div>

						{/* Riepilogo ordine */}
						{orderDetails && (
							<div className="border-t border-border pt-6 mb-8">
								<h3 className="text-xl font-semibold text-foreground mb-4">
									Riepilogo Ordine
								</h3>

								<div className="space-y-3">
									{orderDetails.order_items?.map((item: any) => (
										<div
											key={item.id}
											className="flex justify-between items-center"
										>
											<div className="flex items-center space-x-3">
												<img
													src={productImage(item.products?.image_url)}
													alt={item.products?.name}
													className="w-12 h-12 object-cover rounded"
												/>
												<div>
													<p className="font-medium text-foreground">
														{item.products?.name}
													</p>
													<p className="text-sm text-muted-foreground">
														{item.variant_label ? `${item.variant_label} · ` : ""}Quantità: {item.quantity}
													</p>
												</div>
											</div>
											<p className="font-semibold text-foreground">
												{formatEuro((item.price * item.quantity))}
											</p>
										</div>
									))}

									{orderDetails.shipping_price > 0 && (
										<div className="flex justify-between items-center pt-2 border-t border-border">
											<p className="text-foreground/80">
												Spedizione
											</p>
											<p className="font-semibold text-foreground">
												{formatEuro(orderDetails.shipping_price)}
											</p>
										</div>
									)}

									<div className="flex justify-between items-center pt-4 border-t-2 border-border">
										<p className="text-xl font-bold text-foreground">
											Totale
										</p>
										<p className="text-xl font-bold text-green-600 dark:text-green-400">
											{formatEuro(orderDetails.total_price + orderDetails.shipping_price)}
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Azioni */}
						<div className="flex flex-col sm:flex-row gap-4">
							<Link
								to="/orders"
								className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-lg text-center transition-colors"
							>
								Vedi i Miei Ordini
							</Link>
							<Link
								to="/products"
								className="flex-1 bg-secondary hover:bg-secondary text-foreground font-semibold py-3 px-6 rounded-lg text-center transition-colors"
							>
								Continua lo Shopping
							</Link>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Pagina di errore
	return (
		<div className="min-h-screen bg-gradient-to-br from-destructive/10 to-background py-12 px-4">
			<div className="max-w-2xl mx-auto">
				<div className="bg-card rounded-2xl shadow-2xl p-8 md:p-12">
					{/* Icona di errore */}
					<div className="text-center mb-8">
						<div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 dark:bg-red-900 rounded-full mb-6">
							<XCircle className="w-16 h-16 text-red-600 dark:text-red-400" />
						</div>

						<h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
							{cancelled === "true"
								? "Pagamento Annullato"
								: "Errore nel Pagamento"}
						</h1>

						<p className="text-lg text-muted-foreground mb-6">
							{cancelled === "true"
								? "Hai annullato il processo di pagamento."
								: "Si è verificato un problema durante il processo di pagamento."}
						</p>
					</div>

					{/* Informazioni */}
					<div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-500 p-6 rounded-lg mb-8">
						<h3 className="text-lg font-semibold text-foreground mb-2">
							Non ti preoccupare
						</h3>
						<ul className="text-foreground/80 space-y-2">
							<li>• Non è stato addebitato nessun importo</li>
							<li>• I tuoi articoli sono ancora nel carrello</li>
							<li>• Puoi riprovare quando vuoi</li>
						</ul>
					</div>

					{/* Suggerimenti */}
					<div className="bg-muted p-6 rounded-lg mb-8">
						<h3 className="text-lg font-semibold text-foreground mb-3">
							Cosa puoi fare:
						</h3>
						<ul className="text-foreground/80 space-y-2">
							<li className="flex items-start">
								<span className="text-blue-500 mr-2">→</span>
								<span>Riprova il pagamento con un altro metodo</span>
							</li>
							<li className="flex items-start">
								<span className="text-blue-500 mr-2">→</span>
								<span>Verifica i dati della tua carta</span>
							</li>
							<li className="flex items-start">
								<span className="text-blue-500 mr-2">→</span>
								<span>Contatta la tua banca se il problema persiste</span>
							</li>
							<li className="flex items-start">
								<span className="text-blue-500 mr-2">→</span>
								<span>Scrivici se hai bisogno di assistenza</span>
							</li>
						</ul>
					</div>

					{/* Azioni */}
					<div className="flex flex-col sm:flex-row gap-4">
						<Link
							to="/cart"
							className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-lg text-center transition-colors"
						>
							Torna al Carrello
						</Link>
						<Link
							to="/contact"
							className="flex-1 bg-secondary hover:bg-secondary text-foreground font-semibold py-3 px-6 rounded-lg text-center transition-colors"
						>
							Contattaci
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}
